import { action, observable } from 'mobx';
import api from './api';
import reportApi from '../Report/api';
import dayjs from 'dayjs';
import {
  MeasurementsInterface,
  Pagination,
  RoomDataInterface,
  RoomReportFiltersInterface,
  RoomsReport,
  RoomsReportByBuildingResponseInterface
} from '@app/types/rooms';
import { notification } from 'antd';
import { isUnitOld, toStringTimeFormatter } from '@app/utils/helpers';
import { BuildingInterface, DashboardBuildingReportInterface } from '@app/types/buildings';
import {
  DEFAULT_ROOMS_REPORT_FILTRATION,
  DEFAULT_PAGINATION_LIMIT,
  DEFAULT_PAGE,
  ORDER_MAPPING,
  EMPTY_ROOMS_REPORT_FILTRATION
} from '@app/constants';

export default class DashboardTabbedStore {
  public dubaiTZ: number = +4;
  @observable public beginDate: number | null = null;
  @observable public endDate: number | null = null;
  @observable public loading = false;
  @observable public loadingRoom = false;
  @observable public selectedBuildingId: number | null = null;
  @observable public selectedBuildingReport: DashboardBuildingReportInterface | null = null;
  @observable public buildings: BuildingInterface[] = [];
  @observable public rooms: { [key: number]: RoomsReport[] } = {};
  @observable public actionLoading: boolean = false;
  @observable public selectedRoomId: string = '';
  @observable public roomData: Partial<RoomDataInterface> | null = {};
  @observable public showOnlyActiveRooms: boolean = false;
  @observable public page: number = DEFAULT_PAGE;
  @observable public limit: number = DEFAULT_PAGINATION_LIMIT;
  @observable sort: { [key: string]: string } = {};
  @observable public pagination: Pagination | null = null;
  @observable public roomReportsFilters: RoomReportFiltersInterface = DEFAULT_ROOMS_REPORT_FILTRATION;

  @action.bound switchActiveRoomsRender = (value: boolean) => {
    this.showOnlyActiveRooms = value;
    this.page = 1;
  };

  get dateAsString() {
    if (!this.beginDate || !this.endDate) return null;
    return {
      beginDate: toStringTimeFormatter(this.beginDate),
      endDate: toStringTimeFormatter(this.endDate)
    };
  }

  @action.bound async initialDashboardDataLoadFetch() {
    try {
      this.loading = true;
      await this.getAllBuildings();
      await this.getBuildingReport();
      await await this.getReportRoomsByBuildingData();
    } catch (error) {
      notification.error({
        message: error.name
      });
    } finally {
      this.loading = false;
    }
  }

  @action.bound async getAllBuildings() {
    try {
      this.loading = true;
      this.buildings = await api.getAllBuildings();
      if (this.selectedBuildingId === null && this.buildings.length) {
        this.selectedBuildingId = this.buildings[0].id;
      }
    } catch (error) {
      notification.error({
        message: error.name
      });
    } finally {
      this.loading = false;
    }
  }

  @action.bound async getBuildingReport(buildingId?: number) {
    try {
      this.loading = true;
      this.selectedBuildingReport = await reportApi.fetchBuildingReport(
        buildingId || this.selectedBuildingId!,
        this.beginDate!
      );
    } catch (error) {
      notification.error({
        message: error.name
      });
    } finally {
      this.loading = false;
    }
  }

  @action.bound async getAllBuildingsPolling() {
    try {
      this.buildings = await api.getAllBuildings();
    } catch (error) {
      notification.error({
        message: error.name
      });
    }
  }

  @action.bound async getBuildingReportPolling() {
    try {
      this.selectedBuildingReport = await reportApi.fetchBuildingReport(this.selectedBuildingId!, this.beginDate!);
    } catch (error) {
      notification.error({
        message: error.name
      });
    }
  }

  @action.bound handleDashboardTabChange = async (buildingId: number) => {
    try {
      this.loading = true;
      this.page = 1;
      this.setSelectBuildingId(buildingId);
      await this.getReportRoomsByBuildingData(buildingId);
      await this.getBuildingReport(buildingId);
    } catch (e) {
      notification.error({
        message: e.name
      });
    } finally {
      this.loading = false;
    }
  };

  @action.bound setSelectBuildingId = (buildingId: number) => {
    this.selectedBuildingId = buildingId;
  };

  @action.bound public async getReportRoomsByBuildingData(building?: number | null) {
    if (!building && !this.selectedBuildingId) return;

    try {
      this.loadingRoom = true;
      const { count, data, page, total }: RoomsReportByBuildingResponseInterface = await api.reportRoomsByBuilding(
        building || this.selectedBuildingId!,
        this.showOnlyActiveRooms,
        this.limit,
        this.page,
        this.roomReportsFilters
      );
      this.pagination = { count, total };
      this.rooms[building || this.selectedBuildingId!] = data.map(it => {
        return {
          ...it,
          lastActiveAt: it.lastActiveAt * 1000,
          isOldUnit: isUnitOld(it.lastActiveAt * 1000)
        };
      });
    } catch (error) {
      notification.error({
        message: error.name
      });
    } finally {
      this.loadingRoom = false;
    }
  }

  @action.bound async getRoomDataById() {
    try {
      this.actionLoading = true;
      this.roomData!.roomInfo = await reportApi.getRoomDataById(this.selectedRoomId);
    } catch (error) {
      notification.error({
        message: error.name
      });
    } finally {
      this.actionLoading = false;
    }
  }

  @action.bound async getRoomAlarmsById(roomId?: string) {
    try {
      this.actionLoading = true;

      const alarms = await reportApi.getAlarmsDataByDate(roomId || this.selectedRoomId);

      const sorterAlarms = alarms.sort((a, b) => a.timestamp > b.timestamp);

      this.roomData!.alarms = sorterAlarms;
    } catch (error) {
      notification.error({
        message: error.name
      });
    } finally {
      this.actionLoading = false;
    }
  }

  @action.bound async getRoomMeasurementsById(id: string) {
    if (!id) {
      return notification.warning({ message: 'Room id is missing' });
    }
    try {
      this.actionLoading = true;
      this.selectedRoomId = id;
      const measurements = await reportApi.getRoomMeasurements10MinGranularityById(id, this.beginDate!, this.endDate!);

      this.roomData = {
        ...this.roomData,
        measurements: measurements.map((item: MeasurementsInterface) => ({
          ...item,
          temp: item.roomTemp
        }))
      };
    } catch (error) {
      console.log({ error });
      notification.error({
        message: error.name
      });
    } finally {
      this.actionLoading = false;
    }
  }

  @action.bound setDashboard24HRangeDate = ({ beginDate: startDate, endDate }) => {
    this.beginDate = dayjs(startDate).valueOf();
    this.endDate = dayjs(endDate).valueOf();
  };

  @action.bound async changePage(page: number, limit: number | undefined) {
    this.page = page;

    if (limit) {
      this.limit = limit;
    }
    await this.getReportRoomsByBuildingData();
  }

  @action.bound async handleTableFilterAndSort(sort?: any) {
    this.roomReportsFilters = EMPTY_ROOMS_REPORT_FILTRATION;
    if (sort.column) {
      if (sort.column?.title === 'Alarms') {
        this.roomReportsFilters.alarmsCount = ORDER_MAPPING[sort.order];
      }
      this.roomReportsFilters[sort.field] = ORDER_MAPPING[sort.order];
    } else {
      this.roomReportsFilters = DEFAULT_ROOMS_REPORT_FILTRATION;
    }
    this.page = 1;

    await this.getReportRoomsByBuildingData();
  }
}
